import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { sanitizeCommentHtml } from './commentHtml';
import { opensInPlace } from '../messages/linkPreview';

/**
 * A posted comment, rendered statically.
 *
 * No editor is instantiated here, and that is the whole performance story: a
 * project discussion runs to a hundred posts, and a hundred ProseMirror
 * instances is a hundred schemas, plugin stacks and DOM observers standing by
 * to edit text nobody is editing.
 *
 * ── Making the pills clickable without React owning them ──────────────────
 *
 * The mention pills come out of dangerouslySetInnerHTML, so React has no
 * elements to attach handlers to. One delegated click listener on the
 * container handles all of them — which also means a thread of a hundred
 * comments has a hundred listeners rather than one per mention.
 *
 * The same listener lets the Messages page open a record link in place
 * (onOpenLink, P1 kyu-20260930-1755-polish). It returns true when it opened
 * the link; false, or no handler, leaves the link to the browser as before.
 * A modified click (new tab, new window) is always left to the browser.
 */
export function CommentBody({
  html,
  className = '',
  onOpenLink,
}: {
  html: string;
  className?: string;
  onOpenLink?: (href: string) => boolean;
}) {
  const navigate = useNavigate();
  // Sanitizing is not free and a thread re-renders on every poll.
  const clean = useMemo(() => sanitizeCommentHtml(html), [html]);

  return (
    <div
      className={`comment-body rich-text-view ${className}`.trim()}
      onClick={(event) => {
        const target = event.target as HTMLElement;
        if (onOpenLink !== undefined && opensInPlace(event)) {
          const href = target.closest('a[href]')?.getAttribute('href') ?? null;
          if (href !== null && onOpenLink(href)) {
            event.preventDefault();
            return;
          }
        }
        const pill = target.closest('span[data-type="mention"]');
        if (pill === null) return;
        const href = pill.getAttribute('data-href');
        // The sanitizer strips data-href unless it is an in-app path, so a
        // pill without one is a mention whose target was refused — it stays
        // readable, it just does not navigate.
        if (href === null || !href.startsWith('/')) return;
        event.preventDefault();
        void navigate(href);
      }}
      // Sanitized immediately above by DOMPurify, with the image-host
      // allowlist and the mention-href check re-applied. See commentHtml.ts.
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}
